import React, { useState, useMemo } from 'react'
import {
    useGetAllBusinesses,
    useUpdateBusiness,
    useDeleteBusiness,
} from '../../api/routes/businesses'
import {
    useGetModules,
    useGetBusinessModules,
    useBusinessModuleCheck,
    useInstallModule,
    useUninstallModule,
} from '../../api/routes/modules'
import { Link } from 'react-router-dom'
import {
    Building2,
    ChevronDown,
    ChevronRight,
    MapPin,
    Calendar,
    Cpu,
    Search,
    ArrowUpRight,
    Plus,
    Pencil,
    Trash2,
    Package,
} from 'lucide-react'
import moment from 'moment'
import { Button } from '../../components/ui/button'
import { authClient, hasPermission } from '../../lib/auth-client'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../../components/ui/dialog'
import { toast } from 'react-toastify'

const DEVICES_PER_PAGE = 10

const DeviceStatusBadge = ({ active }: { active: boolean }) => (
    <span
        className={`text-xs font-medium px-2 py-0.5 rounded-full ${
            active
                ? 'bg-green-50 text-green-700 border border-green-200'
                : 'bg-red-50 text-red-700 border border-red-200'
        }`}
    >
        {active ? 'Active' : 'Inactive'}
    </span>
)

const DevicesTable = ({ devices }: { devices: any[] }) => {
    const [page, setPage] = useState(0)
    const [search, setSearch] = useState('')

    const filtered = useMemo(() => {
        if (!search) return devices
        const q = search.toLowerCase()
        return devices.filter(
            (d) =>
                d.name?.toLowerCase().includes(q) ||
                d.serial?.toLowerCase().includes(q) ||
                d.model?.name?.toLowerCase().includes(q)
        )
    }, [devices, search])

    const totalPages = Math.ceil(filtered.length / DEVICES_PER_PAGE)
    const pageDevices = filtered.slice(
        page * DEVICES_PER_PAGE,
        (page + 1) * DEVICES_PER_PAGE
    )

    return (
        <div>
            {devices.length > DEVICES_PER_PAGE && (
                <div className="px-6 py-3 border-b">
                    <div className="relative max-w-xs">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                        <input
                            type="text"
                            placeholder="Search devices..."
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value)
                                setPage(0)
                            }}
                            className="w-full h-8 pl-8 pr-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                        />
                    </div>
                </div>
            )}
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead>
                        <tr className="border-b bg-muted/50">
                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                Name
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider hidden sm:table-cell">
                                Serial
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider hidden md:table-cell">
                                Model
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                Status
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                <span className="sr-only">View</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {pageDevices.map((device: any) => (
                            <tr
                                key={device.id}
                                className="border-b last:border-0 hover:bg-muted/30 transition-colors"
                            >
                                <td className="px-6 py-3 text-sm text-foreground font-medium">
                                    {device.name}
                                </td>
                                <td className="px-6 py-3 text-sm text-muted-foreground font-mono hidden sm:table-cell">
                                    {device.serial}
                                </td>
                                <td className="px-6 py-3 text-sm text-muted-foreground hidden md:table-cell">
                                    {device.model?.name}
                                </td>
                                <td className="px-6 py-3">
                                    <DeviceStatusBadge
                                        active={
                                            device.cloudActive ||
                                            device.cloudActiveOverride
                                        }
                                    />
                                </td>
                                <td className="px-6 py-3 text-right">
                                    <Link
                                        to={`/devices/${device.id}`}
                                        className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1"
                                    >
                                        View
                                        <ArrowUpRight className="h-3 w-3" />
                                    </Link>
                                </td>
                            </tr>
                        ))}
                        {pageDevices.length === 0 && (
                            <tr>
                                <td
                                    colSpan={5}
                                    className="px-6 py-8 text-center text-sm text-muted-foreground"
                                >
                                    {search
                                        ? 'No devices match your search.'
                                        : 'No devices.'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            {totalPages > 1 && (
                <div className="px-6 py-3 border-t flex items-center justify-between">
                    <p className="text-xs text-muted-foreground">
                        Showing {page * DEVICES_PER_PAGE + 1}–
                        {Math.min(
                            (page + 1) * DEVICES_PER_PAGE,
                            filtered.length
                        )}{' '}
                        of {filtered.length} devices
                    </p>
                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => setPage((p) => Math.max(0, p - 1))}
                            disabled={page === 0}
                            className="h-7 px-2.5 text-xs font-medium rounded-md border hover:bg-accent disabled:opacity-40 disabled:pointer-events-none transition-colors"
                        >
                            Previous
                        </button>
                        <button
                            onClick={() =>
                                setPage((p) => Math.min(totalPages - 1, p + 1))
                            }
                            disabled={page >= totalPages - 1}
                            className="h-7 px-2.5 text-xs font-medium rounded-md border hover:bg-accent disabled:opacity-40 disabled:pointer-events-none transition-colors"
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}

const ModulesSection = ({ businessId }: { businessId: string }) => {
    const { data: allModules } = useGetModules()
    const { data: installedModules, isLoading } =
        useGetBusinessModules(businessId)
    const installModule = useInstallModule()
    const uninstallModule = useUninstallModule()

    // Build a map of directly installed module slugs
    const installedSlugs = new Set(
        (installedModules || []).map((bm: any) => bm.module?.slug)
    )

    if (isLoading || !allModules) {
        return (
            <div className="px-6 py-4">
                <div className="animate-pulse h-10 bg-muted rounded" />
            </div>
        )
    }

    return (
        <div className="px-6 py-4">
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <Package className="h-3.5 w-3.5" />
                Modules
            </h4>
            <div className="space-y-2">
                {allModules.map((mod: any) => {
                    const isDirectlyInstalled = installedSlugs.has(mod.slug)

                    return (
                        <ModuleRow
                            key={mod.slug}
                            mod={mod}
                            businessId={businessId}
                            isDirectlyInstalled={isDirectlyInstalled}
                            onInstall={() =>
                                installModule.mutate(
                                    { businessId, moduleSlug: mod.slug },
                                    {
                                        onSuccess: () =>
                                            toast.success(
                                                `${mod.name} installed`
                                            ),
                                        onError: (err: any) =>
                                            toast.error(
                                                err?.response?.data?.message ||
                                                    'Failed to install module'
                                            ),
                                    }
                                )
                            }
                            onUninstall={() =>
                                uninstallModule.mutate(
                                    { businessId, moduleSlug: mod.slug },
                                    {
                                        onSuccess: () =>
                                            toast.success(
                                                `${mod.name} uninstalled`
                                            ),
                                        onError: (err: any) =>
                                            toast.error(
                                                err?.response?.data?.message ||
                                                    'Failed to uninstall module'
                                            ),
                                    }
                                )
                            }
                            installing={installModule.isPending}
                            uninstalling={uninstallModule.isPending}
                        />
                    )
                })}
            </div>
        </div>
    )
}

const ModuleRow = ({
    mod,
    businessId,
    isDirectlyInstalled,
    onInstall,
    onUninstall,
    installing,
    uninstalling,
}: {
    mod: any
    businessId: string
    isDirectlyInstalled: boolean
    onInstall: () => void
    onUninstall: () => void
    installing: boolean
    uninstalling: boolean
}) => {
    const { data: access } = useBusinessModuleCheck(businessId, mod.slug)
    const isInherited = access?.source === 'inherited'
    const hasAccess = isDirectlyInstalled || isInherited

    return (
        <div className="flex items-center justify-between rounded-md border px-3 py-2">
            <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-foreground">
                    {mod.name}
                </span>
                {mod.isPaid && (
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                        Paid
                    </span>
                )}
                {isInherited && access?.inheritedFrom && (
                    <span className="text-[10px] text-muted-foreground">
                        Inherited from {access.inheritedFrom.name}
                    </span>
                )}
                {isDirectlyInstalled && (
                    <span className="text-[10px] font-medium text-green-700">
                        Installed
                    </span>
                )}
            </div>
            <div>
                {hasAccess ? (
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={onUninstall}
                        disabled={isInherited || uninstalling}
                        className="text-xs h-7"
                        title={
                            isInherited
                                ? 'Cannot uninstall inherited modules. Uninstall from the parent business.'
                                : ''
                        }
                    >
                        {uninstalling ? 'Removing...' : 'Uninstall'}
                    </Button>
                ) : (
                    <Button
                        size="sm"
                        onClick={onInstall}
                        disabled={installing}
                        className="text-xs h-7"
                    >
                        {installing ? 'Installing...' : 'Install'}
                    </Button>
                )}
            </div>
        </div>
    )
}

const BusinessCard = ({
    business,
    allBusinesses,
    role,
}: {
    business: any
    allBusinesses: any[]
    role: string | undefined
}) => {
    const [expanded, setExpanded] = useState(false)
    const [editOpen, setEditOpen] = useState(false)
    const [deleteOpen, setDeleteOpen] = useState(false)
    const [editForm, setEditForm] = useState({
        name: business.name,
        address: business.address || '',
        parentBusinessId: business.parentBusiness?.id || '',
    })
    const updateBusiness = useUpdateBusiness()
    const deleteBusiness = useDeleteBusiness()

    const deviceCount = business.devices?.length || 0
    const activeCount =
        business.devices?.filter(
            (d: any) => d.cloudActive || d.cloudActiveOverride
        ).length || 0

    const childBusinesses = allBusinesses.filter(
        (b) => b.parentBusiness?.id === business.id
    )

    const canEdit = role && hasPermission(role, { business: ['update'] })
    const canDelete = role && hasPermission(role, { business: ['delete'] })

    const handleEdit = (e: React.FormEvent) => {
        e.preventDefault()
        if (!editForm.name.trim()) {
            toast.error('Business name is required')
            return
        }
        updateBusiness.mutate(
            {
                id: business.id,
                data: {
                    name: editForm.name.trim(),
                    address: editForm.address.trim(),
                    parentBusinessId: editForm.parentBusinessId || null,
                },
            },
            {
                onSuccess: () => {
                    toast.success('Business updated successfully')
                    setEditOpen(false)
                },
                onError: (error: any) => {
                    toast.error(
                        error?.response?.data?.message ||
                            error.message ||
                            'Failed to update business'
                    )
                },
            }
        )
    }

    const handleDelete = () => {
        deleteBusiness.mutate(business.id, {
            onSuccess: () => {
                toast.success('Business deleted successfully')
                setDeleteOpen(false)
            },
            onError: (error: any) => {
                toast.error(
                    error?.response?.data?.message ||
                        error.message ||
                        'Failed to delete business'
                )
            },
        })
    }

    return (
        <div className="rounded-lg border bg-card">
            <div
                className="px-6 py-5 flex items-center justify-between cursor-pointer hover:bg-muted/30 transition-colors"
                onClick={() => setExpanded(!expanded)}
            >
                <div className="flex items-center gap-4 min-w-0">
                    <div className="h-9 w-9 rounded-md bg-muted flex items-center justify-center flex-shrink-0">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-sm font-semibold text-foreground truncate">
                                {business.name}
                            </h3>
                            {business.parentBusiness && (
                                <span className="text-xs text-muted-foreground flex items-center gap-1 flex-shrink-0">
                                    <ArrowUpRight className="h-3 w-3" />
                                    {business.parentBusiness.name}
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                            {business.address && (
                                <span className="flex items-center gap-1">
                                    <MapPin className="h-3 w-3" />
                                    {business.address}
                                </span>
                            )}
                            <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                Since{' '}
                                {moment(business.registeredAt).format(
                                    'MMM YYYY'
                                )}
                            </span>
                            {childBusinesses.length > 0 && (
                                <span className="flex items-center gap-1">
                                    <Building2 className="h-3 w-3" />
                                    {childBusinesses.length} child-business
                                    {childBusinesses.length !== 1 ? 'es' : ''}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-4 flex-shrink-0 ml-4">
                    {(canEdit || canDelete) && (
                        <div
                            className="flex items-center gap-1"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {canEdit && (
                                <button
                                    onClick={() => {
                                        setEditForm({
                                            name: business.name,
                                            address: business.address || '',
                                            parentBusinessId:
                                                business.parentBusiness?.id ||
                                                '',
                                        })
                                        setEditOpen(true)
                                    }}
                                    className="h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                                >
                                    <Pencil className="h-3.5 w-3.5" />
                                </button>
                            )}
                            {/* {canDelete && (
                                <button
                                    onClick={() => setDeleteOpen(true)}
                                    className="h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-red-600 hover:bg-red-50 transition-colors"
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </button>
                            )} */}
                        </div>
                    )}
                    <div className="text-right hidden sm:block">
                        <div className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                            <Cpu className="h-3.5 w-3.5 text-muted-foreground" />
                            {deviceCount}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                            {activeCount} active
                        </p>
                    </div>
                    {expanded ? (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    ) : (
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    )}
                </div>
            </div>
            {expanded && (
                <div className="border-t">
                    {role === 'systemAdmin' && (
                        <div className="border-b">
                            <ModulesSection businessId={business.id} />
                        </div>
                    )}
                    {deviceCount > 0 ? (
                        <DevicesTable
                            devices={[...business.devices].sort(
                                (a: any, b: any) => a.name.localeCompare(b.name)
                            )}
                        />
                    ) : (
                        <div className="px-6 py-8 text-center">
                            <Cpu className="h-6 w-6 text-muted-foreground mx-auto mb-2" />
                            <p className="text-sm font-medium text-foreground mb-1">
                                No devices
                            </p>
                            <p className="text-xs text-muted-foreground">
                                This business has no devices registered.
                            </p>
                        </div>
                    )}
                </div>
            )}

            <Dialog open={editOpen} onOpenChange={setEditOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Edit Business</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleEdit} className="space-y-4">
                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                Business Name
                            </label>
                            <input
                                value={editForm.name}
                                onChange={(e) =>
                                    setEditForm((f) => ({
                                        ...f,
                                        name: e.target.value,
                                    }))
                                }
                                required
                                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                Address
                            </label>
                            <input
                                value={editForm.address}
                                onChange={(e) =>
                                    setEditForm((f) => ({
                                        ...f,
                                        address: e.target.value,
                                    }))
                                }
                                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                Parent Business
                            </label>
                            <select
                                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                                value={editForm.parentBusinessId}
                                onChange={(e) =>
                                    setEditForm((f) => ({
                                        ...f,
                                        parentBusinessId: e.target.value,
                                    }))
                                }
                            >
                                <option value="">None (Top-level)</option>
                                {allBusinesses
                                    ?.filter((b: any) => b.id !== business.id)
                                    .map((b: any) => (
                                        <option key={b.id} value={b.id}>
                                            {b.name}
                                        </option>
                                    ))}
                            </select>
                        </div>
                        <DialogFooter className="flex justify-end gap-2 pt-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setEditOpen(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={updateBusiness.isPending}
                            >
                                {updateBusiness.isPending
                                    ? 'Saving...'
                                    : 'Save Changes'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Delete Business</DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <p className="text-sm text-foreground">
                            Are you sure you want to permanently delete{' '}
                            <span className="font-semibold">
                                {business.name}
                            </span>
                            ? This action cannot be undone.
                        </p>
                    </div>
                    <DialogFooter className="flex justify-end gap-2">
                        <Button
                            variant="outline"
                            onClick={() => setDeleteOpen(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleDelete}
                            disabled={deleteBusiness.isPending}
                        >
                            {deleteBusiness.isPending
                                ? 'Deleting...'
                                : 'Delete Business'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}

const BusinessesRoute = () => {
    const { data: businesses, isLoading } = useGetAllBusinesses()
    const [search, setSearch] = useState('')
    const { data: session } = authClient.useSession()
    const role = session?.user?.role

    const filtered = useMemo(() => {
        if (!businesses) return []
        if (!search) return businesses
        const q = search.toLowerCase()
        return businesses.filter(
            (b: any) =>
                b.name?.toLowerCase().includes(q) ||
                b.address?.toLowerCase().includes(q)
        )
    }, [businesses, search])

    // Build a map of children by parent ID for recursive tree rendering
    const childrenByParent = useMemo(() => {
        const map = new Map<string | null, any[]>()
        const filteredIds = new Set(filtered.map((b: any) => b.id))
        filtered.forEach((b: any) => {
            const parentId = b.parentBusiness?.id || null
            // If parent is not in the filtered set, treat as root
            const key = parentId && filteredIds.has(parentId) ? parentId : null
            const existing = map.get(key) || []
            existing.push(b)
            map.set(key, existing)
        })
        // Sort children alphabetically within each group
        map.forEach((children) =>
            children.sort((a: any, b: any) => a.name.localeCompare(b.name))
        )
        return map
    }, [filtered])

    const renderBusinessTree = (
        parentId: string | null,
        depth: number
    ): React.ReactNode => {
        const children = childrenByParent.get(parentId) || []
        return children.map((business: any) => (
            <div key={business.id}>
                <div style={{ marginLeft: depth * 24 }}>
                    <BusinessCard
                        business={business}
                        allBusinesses={businesses || []}
                        role={role}
                    />
                </div>
                {renderBusinessTree(business.id, depth + 1)}
            </div>
        ))
    }

    if (isLoading) {
        return (
            <div className="mx-auto py-6 lg:py-10 px-4 sm:px-6 lg:px-8 xl:px-32">
                <div className="animate-pulse space-y-4">
                    <div className="h-7 w-40 bg-muted rounded" />
                    <div className="h-10 w-full sm:w-80 bg-muted rounded-lg" />
                    <div className="space-y-3">
                        <div className="h-20 bg-muted rounded-lg" />
                        <div className="h-20 bg-muted rounded-lg" />
                        <div className="h-20 bg-muted rounded-lg" />
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="mx-auto py-6 lg:py-10 px-4 sm:px-6 lg:px-8 xl:px-32">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-semibold tracking-tight text-foreground">
                        Businesses
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        {businesses?.length || 0} business
                        {businesses?.length !== 1 ? 'es' : ''} registered
                    </p>
                </div>
                {role && hasPermission(role, { business: ['create'] }) && (
                    <Link to="/businesses/register">
                        <Button>
                            <Plus className="h-4 w-4 mr-1.5" />
                            Register Business
                        </Button>
                    </Link>
                )}
            </div>

            {businesses && businesses.length > 3 && (
                <div className="relative max-w-sm mb-4">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <input
                        type="text"
                        placeholder="Search businesses..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full h-9 pl-9 pr-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                    />
                </div>
            )}

            <div className="space-y-3">
                {renderBusinessTree(null, 0)}

                {filtered?.length === 0 && (
                    <div className="rounded-lg border border-dashed p-12 text-center">
                        <Building2 className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
                        <p className="text-sm font-medium text-foreground mb-1">
                            {search ? 'No businesses found' : 'No businesses'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                            {search
                                ? 'Try adjusting your search terms.'
                                : 'No businesses have been registered yet.'}
                        </p>
                    </div>
                )}
            </div>
        </div>
    )
}

export { BusinessesRoute }
